import type { PendingToolApproval, ProjectedWait } from "../../../api/threads/liveMessages";
import type { ToolApprovalOutcome } from "../../../api/threads/toolApprovalsApi";

/**
 * A tool version runs on a PC only after its owner approves it (security review H-1,
 * connector.md "Consent to each tool version"). The connector refuses the turn with this
 * code, and the server keeps the turn waiting until it is approved (TR-B1-01); the current
 * step then says what to approve and where (web-app.md WA-3). The host may answer it in the
 * Thread, wherever they are signed in; the server relays the answer to that PC's connector,
 * which decides (TA-6). Pure.
 */
export const TOOL_CONSENT_REQUIRED = "tool_consent_required";

/** The request an open turn waits on, as its PC's connector holds it: the host may answer it here (TA-6). */
export interface AnswerableRequest {
  readonly threadId: string;
  readonly turnId: string;
  readonly pending: PendingToolApproval;
}

export interface ToolApproval {
  /** The tool's protocol name, for Details and the terminal; the screen names the step (VF0-J1-01). */
  readonly toolName: string;
  /** The step whose run waits, which the screen names by its reviewed name; `null` for an agent's own run of a tool. */
  readonly stepKey: string | null;
  /** The tool version the owner approves: `<item>@<version>`, as the connector lists it. */
  readonly toolReference: string;
  /** In a terminal on the host's PC, naming this server (connector.md, consent to each tool version). */
  readonly connectorCommand: string;
  /** `null` for a run that failed before TR-B1-01, or a connector that reports no request. */
  readonly answerable: AnswerableRequest | null;
}

interface ApprovedTool {
  readonly reference: string;
  readonly name: string;
}

function toolApproval(tool: ApprovedTool, serverOrigin: string, answerable: AnswerableRequest | null, stepKey: string | null): ToolApproval {
  return { toolName: tool.name, stepKey, toolReference: tool.reference, connectorCommand: `dmw-connector tools approve --server ${serverOrigin}`, answerable };
}

/** What the screen calls the run that waits: its step's reviewed name, or `agentsTool` for an agent's own run of a tool (VF0-J1-01). */
export function approvalName(approval: ToolApproval, stepName: (stepKey: string) => string, agentsTool: string): string {
  return approval.stepKey === null ? agentsTool : stepName(approval.stepKey);
}

/** What the host's PC must approve before the step can run, for a refusal of this kind (a run that failed before TR-B1-01). */
export function toolApprovalOf(problemCode: string, tool: ApprovedTool, serverOrigin: string, stepKey: string | null): ToolApproval | null {
  return problemCode === TOOL_CONSENT_REQUIRED ? toolApproval(tool, serverOrigin, null, stepKey) : null;
}

/** The approval a turn waits for, as module C names it (W-22): its tool, and the request the host may answer; `null` without its tool. */
export function awaitedToolApproval(wait: ProjectedWait, threadId: string, serverOrigin: string): ToolApproval | null {
  if (wait.tool == null) return null;
  const pending = wait.approval ?? null;
  return toolApproval(wait.tool, serverOrigin, pending === null ? null : { threadId, turnId: wait.turn_id, pending }, wait.step_key ?? null);
}

/** Who vouches for the version, as the PC's own page words it: its author, and reviewers other than the author. */
export type ToolVouch =
  | { readonly kind: "reviewed"; readonly author: string | null; readonly reviewers: readonly string[] }
  | { readonly kind: "authorOnly"; readonly author: string }
  | { readonly kind: "unknown" };

export function vouchOf(tool: PendingToolApproval["tool"]): ToolVouch {
  const reviewers = tool.reviewers.filter((reviewer) => reviewer !== tool.author);
  if (reviewers.length > 0) return { kind: "reviewed", author: tool.author, reviewers };
  return tool.author === null ? { kind: "unknown" } : { kind: "authorOnly", author: tool.author };
}

/** The words for what the PC's connector made of the host's answer (`threads.toolApproval.outcomes.*`). */
export function outcomeTextKey(outcome: ToolApprovalOutcome, answer: "approved" | "denied"): string {
  if (outcome === "recorded") return answer === "approved" ? "approved" : "declined";
  const keys: Readonly<Record<Exclude<ToolApprovalOutcome, "recorded">, string>> = {
    not_pending: "notPending",
    expired: "expired",
    not_owner: "notOwner",
    no_answer: "noAnswer",
    update_needed: "updateNeeded",
  };
  return keys[outcome];
}
